import {
  ROADMAP_STEP_PROGRESS_LABELS,
  currentRoadmapStep,
  resolveRoadmapSteps,
  type RoadmapObjective,
  type RoadmapStepView,
} from '@isbd/shared';

interface Props {
  formulaKey: string | null | undefined;
  formulaLabel?: string | null;
  roadmap: RoadmapObjective[];
  coachName?: string | null;
  validated?: boolean;
  onAccessStep?: (step: RoadmapStepView) => void;
}

export function RoadmapPanel({
  formulaKey,
  formulaLabel,
  roadmap,
  coachName,
  validated,
  onAccessStep,
}: Props) {
  const steps = resolveRoadmapSteps(roadmap, formulaKey);
  const current = currentRoadmapStep(steps);
  const row1 = steps.filter((s) => s.order <= 5);
  const row2 = steps.filter((s) => s.order > 5);

  return (
    <div className="as-roadmap">
      <div className="as-roadmap-hero">
        <div>
          <h3 className="as-roadmap-title">Feuille de Route de l’Accompagnement</h3>
          <p className="muted" style={{ margin: 0 }}>
            Les 9 étapes clés de votre parcours d’accompagnement
            {formulaLabel ? ` — ${formulaLabel}` : ''}
          </p>
        </div>
        {coachName ? (
          <p className="as-roadmap-coach">
            Conseiller : <strong>{coachName}</strong>
          </p>
        ) : (
          <p className="muted">Conseiller à assigner</p>
        )}
      </div>

      {!validated && roadmap.length === 0 ? (
        <p className="muted">
          Aperçu selon votre formule. Votre conseiller validera les étapes incluses dans votre
          engagement.
        </p>
      ) : null}

      <div className="as-roadmap-layout">
        <section className="as-roadmap-flow">
          <h4>Parcours en 9 étapes</h4>
          <div className="as-flow-row">
            {row1.map((step, i) => (
              <FlowNode
                key={step.key}
                step={step}
                isCurrent={current?.key === step.key}
                showConnector={i < row1.length - 1}
                connectorDone={step.status === 'done' || row1[i + 1]?.status !== 'upcoming'}
              />
            ))}
          </div>
          <div className="as-flow-row as-flow-row-2">
            {row2.map((step, i) => (
              <FlowNode
                key={step.key}
                step={step}
                isCurrent={current?.key === step.key}
                showConnector={i < row2.length - 1}
                connectorDone={step.status === 'done' || row2[i + 1]?.status !== 'upcoming'}
              />
            ))}
          </div>
        </section>

        <aside className="as-roadmap-progress">
          <h4>Progression actuelle</h4>
          <ul className="as-roadmap-list">
            {steps.map((step) => (
              <li key={step.key} className={!step.included ? 'is-na' : undefined}>
                <span className="as-roadmap-list-title">
                  {step.order}. {step.title}
                </span>
                <StepBadge step={step} />
              </li>
            ))}
          </ul>
        </aside>
      </div>

      {current ? (
        <section className="as-roadmap-current">
          <div>
            <p className="eyebrow">Étape actuelle</p>
            <h4 style={{ margin: '0.2rem 0' }}>{current.title}</h4>
            <p className="muted" style={{ margin: 0 }}>
              {current.description}
            </p>
          </div>
          <div className="as-roadmap-current-progress">
            <p className="eyebrow">Avancement de l’étape</p>
            <p className="as-roadmap-pct">{current.progress}%</p>
            <div className="as-progress-track" aria-hidden>
              <div className="as-progress-fill" style={{ width: `${current.progress}%` }} />
            </div>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!current.included}
            onClick={() => onAccessStep?.(current)}
          >
            Accéder
          </button>
        </section>
      ) : (
        <p className="muted">Aucune étape incluse pour cette formule pour le moment.</p>
      )}
    </div>
  );
}

function FlowNode({
  step,
  showConnector,
  connectorDone,
  isCurrent,
}: {
  step: RoadmapStepView;
  showConnector: boolean;
  connectorDone?: boolean;
  isCurrent?: boolean;
}) {
  return (
    <div
      className={`as-flow-node ${!step.included ? 'is-na' : ''} ${isCurrent ? 'is-current' : ''}`}
    >
      <div className="as-flow-node-inner">
        <span className={`as-flow-badge tone-${step.tone}`}>{step.order}</span>
        {!step.included ? (
          <span className="as-na-mark" title="Non applicable">
            ✕
          </span>
        ) : null}
        <span className="as-flow-label">{step.title}</span>
      </div>
      {showConnector ? (
        <span className={`as-flow-connector ${connectorDone ? 'is-done' : ''}`} aria-hidden />
      ) : null}
    </div>
  );
}

function StepBadge({ step }: { step: RoadmapStepView }) {
  if (!step.included) {
    return (
      <span className="as-step-badge na" title="Non pris en charge pour cet engagement">
        ✕ Non applicable
      </span>
    );
  }
  return (
    <span className={`as-step-badge ${step.status}`}>
      {ROADMAP_STEP_PROGRESS_LABELS[step.status]}
    </span>
  );
}
